import {
  type GenerateAtlasOk,
  type GenerateAtlasRequest,
  type GenerateAtlasResponse,
  type RecipeSpec,
} from './protocol';

interface PendingJob {
  resolve(res: GenerateAtlasOk): void;
  reject(err: Error): void;
}

export class TexgenPool {
  private readonly worker: Worker;
  private nextId = 1;
  private readonly pending = new Map<number, PendingJob>();

  constructor() {
    this.worker = new Worker(new URL('./texgen-worker.ts', import.meta.url), { type: 'module' });
    this.worker.addEventListener('message', this.onMessage);
    this.worker.addEventListener('error', this.onError);
  }

  private readonly onMessage = (e: MessageEvent<GenerateAtlasResponse>): void => {
    const res = e.data;
    const job = this.pending.get(res.id);
    if (job === undefined) return;
    this.pending.delete(res.id);
    if (res.ok) job.resolve(res);
    else job.reject(new Error(res.error));
  };

  private readonly onError = (e: ErrorEvent): void => {
    for (const job of this.pending.values()) {
      job.reject(new Error(e.message || 'texgen worker error'));
    }
    this.pending.clear();
  };

  generateAtlas(atlasSize: number, recipes: readonly RecipeSpec[]): Promise<GenerateAtlasOk> {
    const id = this.nextId++;
    const req: GenerateAtlasRequest = {
      id,
      kind: 'generate-atlas',
      atlasSize,
      recipes: [...recipes],
    };
    return new Promise<GenerateAtlasOk>((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.worker.postMessage(req);
    });
  }

  dispose(): void {
    this.worker.removeEventListener('message', this.onMessage);
    this.worker.removeEventListener('error', this.onError);
    this.worker.terminate();
    for (const job of this.pending.values()) job.reject(new Error('texgen pool disposed'));
    this.pending.clear();
  }
}
